import { InstrumentIdentifier } from '@digitalasset/utility-operator-api-ts-client';
import { useOfferTransfer } from '@hooks/mutations/registry/transfer/useOfferTransfer';
import useHoldings from '@hooks/queries/registry/useHoldings';
import useIsPackageAvailableFrom from '@hooks/queries/settings/useIsPackageAvailableFrom';
import React from 'react';
import { v4 as uuidv4 } from 'uuid';
import {
  Box,
  Card,
  Collapse,
  LinearProgress,
  Paper,
  Stack,
  TextField,
  Toolbar,
  Typography,
} from '@mui/material';
import { Holding } from '@daml.js/utility-registry-holding-v0/lib/Utility/Registry/Holding/V0/Holding';
import { TransferOffer } from '@daml.js/utility-registry-v0/lib/Utility/Registry/V0/Holding/Transfer';
import { AsyncButton } from '../../../components/Button/AsyncButton';
import { useParties } from '../../../contexts/PartiesContext';
import TemplateContract from '../../../utils/TemplateContract';
import { fmt } from '../../../utils/common';
import { filterHoldingsByInstrument } from '../../../utils/filterHoldingsByInstrument';
import { filterHoldingsByOwner } from '../../../utils/filterHoldingsByOwner';
import { getMinAmountCids } from '../../../utils/getMinAmountCids';
import { isPositiveFloat } from '../../../utils/isPositiveFloat';
import { isValidAmount } from '../../../utils/validateAmount';
import { useOfferTransferInstruction } from './useOfferTransferInstruction';

interface OfferTransferFormProps {
  holding: TemplateContract<Holding>;
  onClose: () => void;
}
interface FormPayload {
  amount: string;
  reference: string;
  receiver: string;
}
export const OfferTransferForm: React.FC<OfferTransferFormProps> = ({ holding, onClose }) => {
  const { operator, party } = useParties();
  const holdings = useHoldings();

  const offerTransferInstruction = useOfferTransferInstruction();
  const [openReference, setOpenAddReference] = React.useState(false);
  const enable2step = useIsPackageAvailableFrom(
    'utility-registry-app-v0',
    [operator, party],
    '0.3.0',
  );
  const onToggle = () => {
    setOpenAddReference(!openReference);
  };
  const [formPayload, setFormPayload] = React.useState<FormPayload>({
    amount: '',
    reference: '',
    receiver: '',
  });

  const amountWithinBalance = React.useMemo(
    () => isValidAmount(formPayload.amount, holding.payload.amount),
    [formPayload.amount, holding.payload.amount],
  );
  const displayedError = amountWithinBalance ? null : 'Transfer amount exceeds current balance.';
  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    switch (name) {
      case 'amount':
        if (isPositiveFloat(value)) {
          setFormPayload((p) => ({ ...p, [name]: value }));
        }
        break;
      default:
        setFormPayload((p) => ({ ...p, [name]: value }));
    }
  };

  const offerTransfer = useOfferTransfer();

  const handleSubmit = React.useCallback(async () => {
    const payload: TransferOffer = {
      transfer: {
        operator,
        provider: party,
        sender: party,
        registrar: holding.payload.instrument.source,
        receiver: formPayload.receiver,
        instrumentIdentifier: {
          source: holding.payload.instrument.source,
          id: holding.payload.instrument.id,
          scheme: 'RegistrarInternalScheme',
        } as InstrumentIdentifier,
        amount: formPayload.amount,
        reference: formPayload.reference,
        batch: { id: uuidv4(), size: '1', settlementFrom: null, settlementUntil: null },
      },
      senderLabel: '',
    };
    await offerTransfer({ argument: payload });
    onClose();
  }, [
    formPayload.amount,
    holding.payload.instrument.id,
    formPayload.receiver,
    holding.payload.instrument.source,
    formPayload.reference,
    offerTransfer,
    onClose,
    operator,
    party,
  ]);
  const onOfferInstruction = async () => {
    const instrumentHoldings = filterHoldingsByInstrument({
      instrumentId: holding.payload.instrument.id,
      registrar: holding.payload.instrument.source,
      holdings: holdings.data || [],
    });
    const ownedHoldings = filterHoldingsByOwner({
      owner: party,
      holdings: instrumentHoldings,
    });
    const holdingCids = getMinAmountCids({
      requiredAmount: formPayload.amount,
      holdings: ownedHoldings,
    });
    await offerTransferInstruction({
      party,
      receiver: formPayload.receiver,
      amount: formPayload.amount,
      source: holding.payload.instrument.source,
      instrumentId: holding.payload.instrument.id,
      holdingCids,
      reference: formPayload.reference,
    });
    onClose();
  };
  const submitFn = enable2step.data ? onOfferInstruction : handleSubmit;
  const disabled = !formPayload.amount || !amountWithinBalance || !formPayload.receiver;
  if (holdings.isLoading || enable2step.isLoading) {
    return <LinearProgress sx={{ width: '100%' }} />;
  }
  return (
    <Box sx={{ overflow: 'auto', height: '100%', display: 'flex', flexDirection: 'column', p: 2 }}>
      <Box sx={{ mb: 2 }}>
        <Typography sx={{ mb: 0.5 }} variant="body2" color="secondary" fontWeight="bold">
          Send from
        </Typography>
        <Box sx={{ p: 1 }} component={Card} variant="outlined">
          <Typography
            variant="body2"
            sx={{
              border: '1px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {party}
          </Typography>
        </Box>
      </Box>
      <Box sx={{ mb: 2 }}>
        <Typography sx={{ mb: 0.5 }} variant="body2" color="secondary" fontWeight="bold">
          Send to
        </Typography>
        <TextField
          name="receiver"
          onChange={onChange}
          autoFocus
          placeholder="Enter party ID"
          size="small"
          fullWidth
        />
      </Box>
      <Box sx={{ mb: 2 }}>
        <Typography sx={{ mb: 0.5 }} variant="body2" color="secondary" fontWeight="bold">
          Asset
        </Typography>
        <Box
          sx={{ p: 1, overflow: 'hidden', flexDirection: 'column' }}
          component={Card}
          variant="outlined"
        >
          <Typography fontWeight="bold" variant="body2">
            {holding.payload.instrument.id}
          </Typography>
          <Typography
            fontSize={12}
            color="text.secondary"
            sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
          >
            {holding.payload.instrument.source}
          </Typography>
        </Box>
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        <Stack alignItems="center" direction="row">
          <Typography sx={{ mb: 0.5, mr: 1 }} variant="body2" color="secondary" fontWeight="bold">
            Amount
          </Typography>
          <Box sx={{ ml: 'auto', display: 'flex', flexDirection: 'row', overflow: 'hidden' }}>
            <Typography color="text.secondary" sx={{ mr: 1 }} variant="caption">
              Balance:
            </Typography>
            <Typography color="text.secondary" variant="caption">
              {fmt(holding?.payload.amount, 2)}
            </Typography>
            <Typography
              sx={{
                ml: 1,
                maxWidth: '100px',
                textOverflow: 'ellipsis',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
              }}
              color="text.secondary"
              variant="caption"
            >
              {holding.payload.instrument.id}
            </Typography>
          </Box>
        </Stack>
        <TextField
          name="amount"
          placeholder="0.00"
          autoComplete="off"
          error={!amountWithinBalance}
          helperText={formPayload.amount === '' ? null : displayedError}
          value={formPayload.amount}
          onChange={onChange}
          size="small"
        />
      </Box>
      <Box onClick={onToggle} sx={{ cursor: 'pointer', mt: 1, mb: 1 }}>
        <Typography variant="body2" color="text.secondary">
          Add reference
        </Typography>
      </Box>
      <Box>
        <Collapse in={openReference}>
          <TextField
            name="reference"
            placeholder="Input reference"
            sx={{ mb: 1 }}
            size="small"
            fullWidth
            value={formPayload.reference}
            onChange={onChange}
          />
        </Collapse>
      </Box>
      <Toolbar />
      <Box elevation={0} component={Paper} sx={{ position: 'sticky', bottom: 0 }}>
        <AsyncButton
          onClick={submitFn}
          enableDefaultPadding
          variant={disabled ? 'outlined' : 'contained'}
          size="small"
          fullWidth
          sx={{ mt: 0, cursor: disabled ? 'not-allowed' : 'pointer' }}
        >
          Offer
        </AsyncButton>
      </Box>
    </Box>
  );
};
